<template>
  <div class="admin-register-container">
    <!-- Background com efeito visual -->
    <div class="background-overlay"></div>
    
    <!-- Container principal -->
    <div class="register-card">
      <!-- Logo/Header -->
      <div class="register-header">
        <div class="logo-section">
          <i class="fa-solid fa-shield-halved"></i>
          <h1>Registro Administrativo</h1>
        </div>
        <p class="register-subtitle">
          Criação de conta de administrador do sistema
        </p>
      </div>

      <!-- Alerta de segurança -->
      <div class="security-alert">
        <i class="fa-solid fa-exclamation-triangle"></i>
        <div class="alert-content">
          <strong>Acesso Restrito:</strong> Esta página é destinada apenas para criação de contas administrativas iniciais.
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner">
          <i class="fa-solid fa-spinner fa-spin"></i>
          <span>Processando registro...</span>
        </div>
      </div>

      <!-- Formulário -->
      <form v-else @submit.prevent="registerAdmin" class="register-form">
        <div class="form-grid">
          <!-- Nome completo -->
          <div class="input-group">
            <label class="input-label required">
              <i class="fa-solid fa-user"></i>
              Nome Completo
            </label>
            <input 
              type="text" 
              v-model="adminData.name"
              class="form-input"
              placeholder="Digite o nome completo..."
              required
              :disabled="saving"
            />
          </div>

          <!-- Nome de usuário -->
          <div class="input-group">
            <label class="input-label required">
              <i class="fa-solid fa-at"></i>
              Nome de Usuário
            </label>
            <input 
              type="text" 
              v-model="adminData.username"
              class="form-input"
              placeholder="Digite o nome de usuário..."
              required
              autocomplete="off"
              :disabled="saving"
            />
          </div>

          <!-- Telefone -->
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-phone"></i>
              Telefone
            </label>
            <input 
              type="text" 
              v-model="adminData.phone"
              class="form-input"
              placeholder="Digite o telefone (opcional)..."
              :disabled="saving"
            />
          </div>

          <!-- Senha -->
          <div class="input-group">
            <label class="input-label required">
              <i class="fa-solid fa-lock"></i>
              Senha
            </label>
            <div class="password-input-container">
              <input 
                :type="showPassword ? 'text' : 'password'" 
                v-model="adminData.password"
                class="form-input password-input"
                placeholder="Digite a senha..."
                required
                autocomplete="new-password"
                :disabled="saving"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showPassword = !showPassword"
                :disabled="saving"
              >
                <i :class="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>

          <!-- Confirmar senha -->
          <div class="input-group">
            <label class="input-label required">
              <i class="fa-solid fa-lock"></i>
              Confirmar Senha
            </label>
            <div class="password-input-container">
              <input 
                :type="showConfirmPassword ? 'text' : 'password'" 
                v-model="adminData.confirmPassword"
                class="form-input password-input"
                placeholder="Confirme a senha..."
                required
                autocomplete="new-password"
                :disabled="saving"
              />
              <button 
                type="button" 
                class="password-toggle"
                @click="showConfirmPassword = !showConfirmPassword"
                :disabled="saving"
              >
                <i :class="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Informações de segurança -->
        <div class="security-info">
          <div class="info-item">
            <i class="fa-solid fa-check"></i>
            <span>O usuário será criado com privilégios de administrador</span>
          </div>
          <div class="info-item">
            <i class="fa-solid fa-check"></i>
            <span>Acesso completo a todas as funcionalidades do sistema</span>
          </div>
          <div class="info-item">
            <i class="fa-solid fa-check"></i>
            <span>Capacidade de gerenciar outros usuários e configurações</span>
          </div>
        </div>

        <!-- Botões -->
        <div class="form-actions">
          <button 
            type="submit" 
            class="btn-register"
            :disabled="saving || !isFormValid"
          >
            <i v-if="saving" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-user-shield"></i>
            {{ saving ? 'Criando Conta...' : 'Criar Conta Administrativa' }}
          </button>
          
          <router-link to="/login" class="btn-back">
            <i class="fa-solid fa-arrow-left"></i>
            Voltar ao Login
          </router-link>
        </div>
      </form>

      <!-- Mensagem de sucesso -->
      <div v-if="registrationSuccess" class="success-message">
        <div class="success-content">
          <i class="fa-solid fa-check-circle"></i>
          <div class="success-text">
            <h3>Conta criada com sucesso!</h3>
            <p>O usuário administrador foi registrado. Você pode fazer login agora.</p>
          </div>
        </div>
        <router-link to="/login" class="btn-login">
          <i class="fa-solid fa-sign-in-alt"></i>
          Ir para Login
        </router-link>
      </div>
    </div>

    <!-- Footer com informações -->
    <div class="register-footer">
      <p>
        <i class="fa-solid fa-shield-alt"></i>
        Sistema de Apostas - Registro Administrativo
      </p>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";

export default {
  name: "AdminRegister",
  data() {
    return {
      adminData: {
        name: "",
        username: "",
        phone: "",
        password: "",
        confirmPassword: "",
        role: 0 // ADMIN role
      },
      showPassword: false,
      showConfirmPassword: false,
      loading: false,
      saving: false,
      registrationSuccess: false
    };
  },
  computed: {
    isFormValid() {
      return (
        this.adminData.name.trim() !== "" &&
        this.adminData.username.trim() !== "" &&
        this.adminData.password.trim() !== "" &&
        this.adminData.confirmPassword.trim() !== "" &&
        this.adminData.password === this.adminData.confirmPassword &&
        this.adminData.password.length >= 6
      );
    }
  },
  methods: {
    validateForm() {
      // Validar campos obrigatórios
      if (!this.adminData.name.trim()) {
        toast.error("Nome completo é obrigatório!");
        return false;
      }

      if (!this.adminData.username.trim()) {
        toast.error("Nome de usuário é obrigatório!");
        return false;
      }

      if (!this.adminData.password.trim()) {
        toast.error("Senha é obrigatória!");
        return false;
      }

      if (this.adminData.password.length < 6) {
        toast.error("A senha deve ter pelo menos 6 caracteres!");
        return false;
      }

      if (this.adminData.password !== this.adminData.confirmPassword) {
        toast.error("As senhas não coincidem!");
        return false;
      }

      // Validar nome de usuário (apenas alfanuméricos e alguns símbolos)
      const usernameRegex = /^[a-zA-Z0-9_.-]+$/;
      if (!usernameRegex.test(this.adminData.username)) {
        toast.error("Nome de usuário deve conter apenas letras, números, ponto, hífen ou underscore!");
        return false;
      }

      return true;
    },

    async registerAdmin() {
      if (!this.validateForm()) {
        return;
      }

      this.saving = true;
      try {
        // Preparar dados para envio
        const userData = {
          name: this.adminData.name.trim(),
          username: this.adminData.username.trim(),
          phone: this.adminData.phone.trim() || null,
          password: this.adminData.password,
          role: 0, // ADMIN
          isActive: true
        };

        // Chamar serviço de registro usando endpoint Insert padrão
        await mainService.insertUser(userData);
        
        this.registrationSuccess = true;
        toast.success("Conta administrativa criada com sucesso!");
        
        // Limpar formulário
        this.adminData = {
          name: "",
          username: "",
          phone: "",
          password: "",
          confirmPassword: "",
          role: 0
        };

      } catch (error) {
        console.error("Erro ao registrar administrador:", error);
        toast.error(handleErrorMessage(error));
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>

<style scoped>
.admin-register-container {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.background-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%);
  pointer-events: none;
}

.register-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  width: 100%;
  max-width: 600px;
  position: relative;
  z-index: 1;
}

[data-theme="dark"] .register-card {
  background: rgba(30, 30, 30, 0.95);
  box-shadow: 
    0 25px 50px rgba(0, 0, 0, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.1);
}

.register-header {
  text-align: center;
  margin-bottom: 2rem;
}

.logo-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1rem;
}

.logo-section i {
  font-size: 3rem;
  color: #667eea;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.logo-section h1 {
  font-size: 2.2rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.register-subtitle {
  color: var(--text-muted);
  font-size: 1.1rem;
  margin: 0;
}

.security-alert {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
  color: white;
  border-radius: 12px;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
}

.security-alert i {
  font-size: 1.5rem;
  flex-shrink: 0;
}

.alert-content {
  flex: 1;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 3rem;
  color: var(--text-muted);
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 1.1rem;
}

.loading-spinner i {
  font-size: 1.5rem;
  color: #667eea;
}

.register-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
}

.input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.input-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.input-label.required::after {
  content: "*";
  color: #e74c3c;
  margin-left: 0.25rem;
}

.input-label i {
  color: #667eea;
  width: 16px;
}

.form-input {
  padding: 1rem;
  border: 2px solid #e1e8ed;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: white;
}

[data-theme="dark"] .form-input {
  background: var(--bg-secondary);
  border-color: var(--border-color);
  color: var(--text-primary);
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.password-input-container {
  position: relative;
}

.password-input {
  padding-right: 3rem;
}

.password-toggle {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--text-muted);
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: color 0.3s ease;
}

.password-toggle:hover {
  color: var(--text-primary);
}

.password-toggle:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.security-info {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 1.5rem;
  margin: 1rem 0;
}

[data-theme="dark"] .security-info {
  background: var(--bg-secondary);
  border-color: var(--border-color);
}

.info-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: var(--text-secondary);
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-item i {
  color: #28a745;
  font-size: 0.9rem;
  flex-shrink: 0;
}

.form-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
}

.btn-register {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-register:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
}

.btn-register:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.btn-back {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: transparent;
  color: var(--text-muted);
  border: 2px solid var(--border-color);
  border-radius: 12px;
  text-decoration: none;
  font-weight: 500;
  transition: all 0.3s ease;
}

.btn-back:hover {
  color: var(--text-primary);
  border-color: var(--text-primary);
  background: rgba(102, 126, 234, 0.05);
}

.success-message {
  text-align: center;
  padding: 2rem;
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  border-radius: 16px;
  margin-top: 2rem;
}

.success-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.success-content i {
  font-size: 3rem;
}

.success-text h3 {
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
}

.success-text p {
  margin: 0;
  opacity: 0.9;
}

.btn-login {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  background: white;
  color: #28a745;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-login:hover {
  background: #f8f9fa;
  transform: translateY(-1px);
}

.register-footer {
  margin-top: 2rem;
  text-align: center;
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
}

.register-footer i {
  margin-right: 0.5rem;
}

/* Responsividade */
@media (max-width: 768px) {
  .admin-register-container {
    padding: 1rem;
  }

  .register-card {
    padding: 2rem;
  }

  .logo-section {
    flex-direction: column;
    gap: 0.5rem;
  }

  .logo-section h1 {
    font-size: 1.8rem;
  }

  .form-grid {
    grid-template-columns: 1fr;
  }

  .form-actions {
    gap: 0.75rem;
  }
}

@media (max-width: 480px) {
  .register-card {
    padding: 1.5rem;
  }

  .logo-section h1 {
    font-size: 1.5rem;
  }

  .security-alert {
    flex-direction: column;
    text-align: center;
  }
}
</style> 